import type { User } from '@fuuze/shared';
import { FlexColumn, FuuzeSelectComponent } from '@fuuze/shared';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store/store';

type PodcastSelectorProps = {
  onAddPodcast: () => void;
  user: User;
};

export function PodcastSelector({ onAddPodcast, user }: PodcastSelectorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [setPodcastSelected, podcastSelected] = useStore(
    useShallow(state => [state.setPodcastSelected, state.podcastSelected]),
  );
  const { watch, control, reset, setValue } = useForm<{ channelId: string }>({
    defaultValues: {
      channelId: user.ownedChannels?.[0]?._id.toString(),
    },
  });

  const options = useMemo(() => {
    const options = (user.ownedChannels || []).map(podcast => ({
      value: podcast._id.toString(),
      label: podcast.title,
      key: podcast._id.toString(),
    }));
    options.push({ value: 'add-podcast', key: 'add-podcast', label: t('components.podcastSelector.addPodcast') });
    return options;
  }, [user]);

  useEffect(() => {
    if (!podcastSelected) return;
    setValue('channelId', podcastSelected._id);
  }, [podcastSelected]);

  useEffect(() => {
    const subscription = watch((value, { type }) => {
      if (!type) {
        // Return early if the change is made programmatically (by the effect setting a value for example)
        return;
      }
      if (value.channelId) {
        if (value.channelId === 'add-podcast') {
          onAddPodcast();

          // To put back the previous selected podcast instead of keeping the "Add podcast" value in select
          reset({
            channelId: useStore.getState().podcastSelected._id,
          });
        } else {
          const channel = user.getOwnedChannel(value.channelId);
          setPodcastSelected(channel!._id, channel!.slug);
          navigate('/creator/podcast');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <FlexColumn style={{ gap: 4 }}>
      <Controller
        name='channelId'
        control={control}
        render={({ field }) => (
          <FuuzeSelectComponent
            RootProps={{ items: options, value: field.value, onValueChange: field.onChange }}
            label={t('components.podcastSelector.label')}
          />
        )}
      />
    </FlexColumn>
  );
}
